import { memo, ReactNode, useEffect, useRef, useState } from "react";

import BoxContainer from "@hl/base-components/lib/BoxContainer";
import {
  ZipDropzone,
  ZipDropzoneProps,
} from "@hl/base-components/lib/ZipDropzone";
import { ErrorTriangle } from "@hl/base-components/lib/assets/icons.generated/index";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { STATUS_COLOR } from "@hl/base-components/lib/theme/colors";
import UploadFileProgress from "@hl/base-components/lib/upload/UploadFileProgress";
import {
  UploadStatus,
  useUploadManager,
} from "@hl/base-components/lib/upload/context";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks/useFeatureFlags";
import { logError } from "@hl/shared-features/lib/services/logger";
import { Button, Group, Stack, Text, useMantineTheme } from "@mantine/core";
import { FileWithPath } from "@mantine/dropzone";
import axios, { AxiosError } from "axios";
import { FileError } from "react-dropzone";

import UnzipProgress from "~/features/collections/forms/CreateCollectionMultistepForm/UnzipProgress";

import { useGetUploadUrlForSeriesMutation } from "../../queries/draft.graphql.generated";

import { UPLOAD_ERROR } from "./CollectionMediaUploader";
import { useCreateCollectionState } from "./context";

export type InternalState =
  | "not-started"
  | "fetching-urls"
  | "uploading-file"
  | "finished";

export type ZipUploadProps = {
  mediaError: string;
  onMediaChanged: (mediaUrl: string | null) => void;
  warningErrorCodes?: string[];
  onResetUpload?(): void;
  onInternalStateChange?(uploadState: InternalState): void;
} & ZipDropzoneProps;

//3.5GB
const MAX_SIZE_MB = 3500;
const MAX_SIZE_GB = MAX_SIZE_MB / 1000;
//20GB
const INCREASED_MAX_SIZE_MB = 20000;
const INCREASED_MAX_SIZE_GB = INCREASED_MAX_SIZE_MB / 1000;

const ZipUpload = ({
  mediaError: errorFromProps,
  onMediaChanged,
  warningErrorCodes,
  onResetUpload,
  onReject,
  onInternalStateChange,
  ...rest
}: ZipUploadProps) => {
  const { state, dispatch } = useUploadManager();
  const { state: draftState, dispatch: collectionDispatch } =
    useCreateCollectionState();
  const [getUploadUrl] = useGetUploadUrlForSeriesMutation();
  const increaseZipLimit = useFeatureFlags(
    FEATURE_FLAGS.ZIP_INCREASE_FILE_SIZE_LIMIT
  );

  const uploadAbortController = useRef<AbortController>(new AbortController());

  const [mediaFile, setMediaFile] = useState<FileWithPath | null>(null);
  const [mediaError, setMediaError] = useState<string>(errorFromProps);
  const [isMediaUploading, setIsMediaUploading] = useState<boolean>(false);
  const [errors, setErrors] = useState<FileError[]>([]);
  const [warnings, setWarnings] = useState<FileError[]>([]);
  const [stagingFiles, setStagingFiles] = useState<FileWithPath[]>([]);

  const resetUpload = () => {
    setErrors([]);
    setWarnings([]);
    setStagingFiles([]);
    onResetUpload?.();
    onInternalStateChange?.("not-started");
  };

  /**
   * Aborts the current upload if the upload context was reset
   */
  useEffect(() => {
    if (state.status === UploadStatus.NOT_STARTED) {
      uploadAbortController.current.abort();
      uploadAbortController.current = new AbortController();
    }
  }, [uploadAbortController.current, state]);

  useEffect(() => {
    setMediaError(errorFromProps);
  }, [errorFromProps]);

  const onFileSelected = (files: FileWithPath[]) => {
    const file = files[0];
    const sizeAllowed = increaseZipLimit
      ? INCREASED_MAX_SIZE_MB * 1000000
      : MAX_SIZE_MB * 1000000;
    if (file.size > sizeAllowed) {
      const error = `The file size cannot be greater than ${
        increaseZipLimit ? INCREASED_MAX_SIZE_GB : MAX_SIZE_GB
      }GB.`;
      setMediaError(error);
    } else {
      setMediaFile(file);
      setMediaError("");
      return true;
    }
    return false;
  };

  const getZipMediaUrls = async (mime: string) => {
    const getUrlResponse = await getUploadUrl({
      variables: { data: { mime } },
    });

    const metadata = getUrlResponse?.data?.getUploadUrlForSeries?.metadata;
    if (!metadata) {
      throw new Error(
        `[ZipUpload] Unexpected response from getUploadUrlForSeriesContractUri: ${JSON.stringify(
          { getUrlResponse }
        )}`
      );
    }

    const { url, publicUrl } = metadata;
    if (!url || !publicUrl) {
      throw new Error(
        `[ZipUpload] Unexpected upload media metadata: ${JSON.stringify({
          metadata,
        })}`
      );
    }

    return { url, publicUrl };
  };

  /**
   * Upload media files and replaces the local blob URLs with the public CDN ones.
   */
  const uploadMediaFileToS3 = async (file: File) => {
    let uploadStarted = false;
    try {
      onInternalStateChange?.("fetching-urls");
      setIsMediaUploading(true);
      const { url, publicUrl } = await getZipMediaUrls(file.type);
      onMediaChanged(publicUrl);

      onInternalStateChange?.("uploading-file");

      uploadStarted = true;
      dispatch({
        type: "UPLOAD_STARTED",
        payload: {
          startTimestamp: new Date(),
          totalBytes: file.size,
          file,
          zipBucketName: publicUrl.split("/").pop() ?? "",
        },
      });

      const response = await axios.put(url, file, {
        headers: {
          "Content-type": file.type,
        },
        signal: uploadAbortController.current?.signal,
        onUploadProgress: (progressEvent) => {
          dispatch({
            type: "UPLOAD_PROGRESSED",
            payload: {
              currentBytes: progressEvent.loaded,
            },
          });
        },
      });

      dispatch({
        type: "UPLOAD_SUCCEEDED",
        payload: {
          data: response.data,
          httpStatus: response.status,
        },
      });
      collectionDispatch({
        type: "UPDATE_UNZIPPED",
        payload: {
          unzipped: false,
        },
      });
    } catch (error) {
      logError({
        error,
        message: "[CollectionMediaUploader] Failed to fetch upload URL",
      });

      if (
        !(axios.isAxiosError(error) && error.code === AxiosError.ERR_CANCELED)
      ) {
        setMediaError(UPLOAD_ERROR);
      }

      setMediaFile(null);
      onMediaChanged(null);

      if (uploadStarted) {
        dispatch({
          type: "UPLOAD_FAILED",
          payload: {
            httpStatus: axios.isAxiosError(error) ? error.status : undefined,
            error: axios.isAxiosError(error)
              ? new Error(error.message)
              : error instanceof Error
              ? error
              : undefined,
            errorCode: axios.isAxiosError(error) ? error.code : undefined,
          },
        });
      }
    } finally {
      onInternalStateChange?.("finished");
      setIsMediaUploading(false);
    }
  };

  useEffect(() => {
    if (mediaFile) {
      // runs async
      uploadMediaFileToS3(mediaFile);
    }
  }, [mediaFile]);

  const uploadFinished = state.status === UploadStatus.FINISHED;
  const unzipFinished = draftState.unzipped;
  if (
    state.status === UploadStatus.IN_PROGRESS ||
    state.status === UploadStatus.FINISHED
  ) {
    return (
      <>
        {uploadFinished && !unzipFinished ? (
          <UnzipProgress />
        ) : !uploadFinished ? (
          <UploadFileProgress />
        ) : (
          <></>
        )}
      </>
    );
  }

  if (
    state.status === UploadStatus.ERROR &&
    state.errorCode !== AxiosError.ERR_CANCELED
  ) {
    return (
      <ErrorContainer>
        <Text size="sm" color={STATUS_COLOR.ERROR} mt={8}>
          Something went wrong while uploading your collection.
        </Text>
        <Button color={SECONDARY_COLOR} onClick={resetUpload} miw={240} mt={20}>
          Try again
        </Button>
      </ErrorContainer>
    );
  }

  if (errors.length || warnings.length) {
    return (
      <ErrorContainer status={errors.length ? "error" : "warning"}>
        <Text size="sm" mt={8}>
          {!!errors.length && (
            <ErrorList
              title={warnings.length ? "Errors" : undefined}
              showAsList={errors.length > 1 || !!warnings.length}
              errors={errors}
            />
          )}
          {!!warnings.length && (
            <ErrorList
              title={errors.length ? "Warnings" : undefined}
              showAsList={warnings.length > 1 || !!errors.length}
              errors={warnings}
            />
          )}
        </Text>
        {errors.length ? (
          <Button
            color={SECONDARY_COLOR}
            onClick={resetUpload}
            miw={240}
            mt={20}
          >
            Try again
          </Button>
        ) : (
          <Group mt={20}>
            <Button color={SECONDARY_COLOR} onClick={resetUpload} miw={240}>
              Upload new .zip
            </Button>
            <Button
              miw={240}
              onClick={() => {
                setErrors([]);
                setWarnings([]);
                setMediaError("");
                uploadMediaFileToS3(stagingFiles[0]);
              }}
            >
              Upload anyway
            </Button>
          </Group>
        )}
      </ErrorContainer>
    );
  }

  return (
    <Stack spacing={4}>
      <ZipDropzone
        loading={isMediaUploading}
        maxSizeInMb={increaseZipLimit ? INCREASED_MAX_SIZE_MB : MAX_SIZE_MB}
        onDrop={onFileSelected}
        onReject={(rejections) => {
          onReject?.(rejections);
          const rejection = rejections[0];
          const warnings = rejection.errors.filter((rejection) =>
            warningErrorCodes?.includes(rejection.code)
          );
          const errors = rejection.errors.filter(
            (rejection) => !warningErrorCodes?.includes(rejection.code)
          );

          setErrors(errors);
          setWarnings(warnings);
        }}
        setStagingFiles={setStagingFiles}
        {...rest}
      />

      {mediaError && (
        <Text size="sm" color={STATUS_COLOR.ERROR}>
          {mediaError}
        </Text>
      )}
    </Stack>
  );
};

const ErrorContainer = ({
  status = "error",
  children,
}: {
  status?: "error" | "warning";
  children: ReactNode;
}) => {
  const theme = useMantineTheme();

  return (
    <BoxContainer p={32}>
      <Stack align="center" spacing={0}>
        <ErrorTriangle
          color={
            status === "error"
              ? theme.colors.errorStatus[0]
              : theme.colors.warningStatus[0]
          }
          width={24}
          height={24}
        />
        {children}
      </Stack>
    </BoxContainer>
  );
};

const ErrorList = ({
  title,
  showAsList,
  errors,
}: {
  title?: string;
  showAsList?: boolean;
  errors: FileError[];
}) => {
  return (
    <div>
      {showAsList ? (
        <ul style={{ padding: 0 }}>
          {title}
          {errors.map((error) => (
            <li key={error.code}>{error.message}</li>
          ))}
        </ul>
      ) : (
        <div>
          {title}
          {errors.map((error) => (
            <div key={error.code}>{error.message}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(ZipUpload);
